/**
 * THE SHEET, IN WORDS A BROWSER CAN CARRY.
 *
 * -- WHAT WAS MISSING, MEASURED ON THE SHIPPED BUILD ---------------------------
 *
 * `dist/world.html` at 1920x1080, dpr 1, measured 2026-09-03 with Chrome's own
 * accessibility tree: FOUR nodes, and two of them are the document. The tree is
 * `RootWebArea "Reelin"` -> ignored -> ignored -> `main` (no name) -> `Canvas`
 * (no name, no role, no fallback). Every word in this game - the level, the coins,
 * the personal best, the catch, the twenty-four species, the sentence that says `L`
 * opens the ledger - is painted into that canvas as one-bit glyphs on the 960x540
 * grid. A player who cannot see the screen gets an unlabelled landmark and an
 * unlabelled bitmap. Five focusable elements exist in the page and MEASURED ZERO of
 * them render in a shipped build, so Tab leaves the document on the first press.
 *
 * CONTRACT section 6 requires readable scalable text. There was none: browser zoom
 * changes which whole-number grain `fitToBox` picks and therefore how many CSS
 * pixels an art pixel spans, but it does not change how big the type is on the
 * reader's actual screen, because the two cancel. Past about 200% zoom the box falls
 * under 960x540 CSS and the game shows its refusal notice instead. Zoom is not a
 * degraded route to bigger type here; it is a route to no game.
 *
 * -- WHAT THIS FILE IS ---------------------------------------------------------
 *
 * The same `ChartCopy` the painter is handed, said in sentences. It is pure, it holds
 * no DOM and no state, and it is the ONLY place the chart's words are turned into
 * prose. `src/hud/reading.ts` writes what this returns into the page; nothing else
 * composes a word.
 *
 * THE ANTI-DRIFT ARGUMENT IS THE SAME ONE `hudCopy` MAKES, one layer further out.
 * `hudCopy` exists so the fence that proves every earned field moves a pixel measures
 * the pixels the game actually draws. This exists so the words a screen reader hears
 * are the words the canvas paints - not a second description of them. One `ChartCopy`
 * is built per step in `world-entry.ts`; `paintChart` draws it and `chartSpeech` says
 * it, and `speech.test.ts` fails if a written field of that object reaches the paint
 * and not the speech.
 *
 * IT RESPECTS THE PAINTER'S OWN GATES rather than inventing kinder ones. The aside is
 * dropped at `asideInk === 0` because that is when the painter stops drawing it
 * (chart.ts:553); the ledger is dropped at `lift <= 0` for the same reason
 * (chart.ts:575); the record is dropped with its pole. A surface that spoke a line
 * the sheet had already let go of would be describing a different game.
 */

import { EXIT_WORDS, LEAF_WORDS, type ChartCopy, type Leaf, type LedgerRow } from '../render/chart';
import { speciesById } from '../sim/index';

/** One row of the ledger, said. `text` carries everything; the flags are for the mark. */
export interface SpokenRow {
  readonly text: string;
  /** The row in force - the species being fished for, or the place the boat is lying at. */
  readonly marked: boolean;
  /** False for a locked species. Nothing else is unreachable. */
  readonly reachable: boolean;
}

export interface Speech {
  /**
   * The standing lines, in the order the sheet writes them: the phrase, the quarry pole,
   * the foot. An empty line is dropped rather than said as a silence.
   */
  readonly lines: readonly string[];
  /** The transient line the sheet is speaking, or '' once it has dissolved. */
  readonly aside: string;
  readonly open: boolean;
  /** The leaf's own heading, or '' while the book is shut. */
  readonly heading: string;
  readonly leaf: Leaf;
  readonly rows: readonly SpokenRow[];
  /** The row under the hand, or -1. Index into `rows`. */
  readonly cursor: number;
  /** The way out, said, or '' when the boat is already on its own course. */
  readonly exit: string;
  /**
   * WHAT THE KEYS DO. The sheet prints no affordance for opening itself and is not going
   * to start; this is the one surface where naming them costs no pixel of the picture.
   *
   * Every key here is a key the running game binds, and `speech.test.ts` holds this list
   * against `keyIntent` so a rebinding cannot leave a lie behind.
   */
  readonly keys: readonly string[];
}

/** "Herring" from a species id, or the id itself if this build has lost the row. */
function speciesName(id: string): string {
  return speciesById(id)?.name ?? id;
}

/**
 * THE QUARRY POLE, SAID. The sheet draws an animal, a count under it and the record
 * under that; three marks, one of which is a picture. In words it is one sentence,
 * because a fish that has to be named is not a picture any more.
 */
function poleLine(copy: ChartCopy): string {
  if (copy.markSpecies === null) return '';
  const name = speciesName(copy.markSpecies);
  const landed = `${copy.markCount} landed`;
  return copy.record.length > 0 ? `${name}: ${landed}, ${copy.record}.` : `${name}: ${landed}.`;
}

/**
 * A LEDGER ROW, SAID, and the tally column is the reason the leaf has to be known here.
 *
 * `LedgerRow.count` is a landed count on the species leaf and LEAGUES OFF on the places
 * leaf - the same field carrying two units, which is exactly the failure chart-copy.ts
 * heads itself with ("a figure wears the unit of the thing it measures"). The sheet gets
 * away with a bare figure because the heading over the column says which leaf you are on.
 * A sentence has no column, so it says the unit.
 */
function rowLine(row: LedgerRow, leaf: Leaf): string {
  const { name, count, level, marked } = row;
  let text: string;
  if (leaf === 'places') {
    const n = count ?? 0;
    text = `${name}, ${n} ${n === 1 ? 'league' : 'leagues'} off`;
  } else if (count === null) {
    text = `${name}, opens at level ${level}`;
  } else {
    text = `${name}, ${count} landed`;
  }
  if (marked) text = leaf === 'places' ? `${text}, the boat is lying here` : `${text}, being fished for`;
  // THE ROW'S MIDDLE COLUMN, SAID. The sheet draws it beside the name in the wash; a sentence
  // has no columns, so it goes on the end as its own clause. Dropping it here would leave the
  // plain-type page describing a leaf the canvas no longer draws, which is the exact drift the
  // header of this file exists to stop.
  const note = row.note ?? '';
  return note.length > 0 ? `${text}. ${note}.` : text;
}

/**
 * THE KEYS, and only the ones that can do something in the state the sheet is in.
 *
 * R is named while `copy.exit` is true and at no other time, for the same reason the
 * sheet draws it then and at no other time: it is a control precisely because it is
 * temporary, and a page that listed it permanently would be printing an affordance
 * for something that is not there.
 */
function keyLines(copy: ChartCopy, ledger: boolean): string[] {
  const keys: string[] = [];
  // CONTRACT 1.5aa: outside ?dev the canvas ledger is shut for good, so the page names the paper
  // sheets that do its jobs instead of a key that would lie. C and G are the host's keys.
  if (!ledger) keys.push('C opens the chart: choose what to fish for and where to sail.', 'G opens the field guide.');
  else keys.push(copy.lift > 0 ? 'L closes the ledger. Escape closes it too.' : 'L opens the ledger.');
  if (ledger && copy.lift > 0) {
    keys.push('Up and down move between rows. Left and right turn the leaf.');
    keys.push('Enter chooses the row under the hand.');
  }
  if (copy.exit) keys.push('R gives the boat back to its own course.');
  keys.push('S quiets the sound.');
  keys.push('T turns this plain-type page off.');
  return keys;
}

/**
 * EVERYTHING THE SHEET SAYS, SAID.
 *
 * Pure. Give it the object the painter was given and it returns the same sheet in
 * sentences: no field of the save is read here, no clock, no view.
 */
export function chartSpeech(copy: ChartCopy, options: { readonly ledger?: boolean } = {}): Speech {
  // `ledger` is whether this build lets anyone open the canvas ledger: ?dev yes, a player no.
  const ledger = options.ledger ?? true;
  const open = copy.lift > 0;
  const lines = [copy.phrase, poleLine(copy), copy.foot].filter((s) => s.length > 0);
  const rows = open
    ? copy.ledger.map((r) => ({
        text: rowLine(r, copy.leaf),
        marked: r.marked,
        reachable: r.reachable,
      }))
    : [];
  return {
    lines,
    aside: copy.asideInk > 0 ? copy.aside : '',
    open,
    heading: open ? LEAF_WORDS[copy.leaf] : '',
    leaf: copy.leaf,
    rows,
    cursor: open && copy.cursor >= 0 && copy.cursor < rows.length ? copy.cursor : -1,
    exit: copy.exit ? EXIT_WORDS : '',
    keys: keyLines(copy, ledger),
  };
}
